// Helpers for Ad-Hoc model versions.
//
// Every version is its own clients/{id} doc. A version points at the first model of its chain (the family
// root) through `version_family` and carries its position in `version_number`. The root is never written:
// a model without those fields is v1 of the family named after its own id. The fields are set only by the
// modelVersions-setModelVersion Cloud Function (functions/models/modelVersions.js).

export function versionFamilyId (model) {
  return model.version_family || model.id
}

export function versionNumber (model) {
  return model.version_number || 1
}

// Map of family id -> versions sorted by number (v1 first). Only the models in `models` are considered, so
// for a user without access to the latest version, the latest one they can see is their current version.
export function groupByFamily (models) {
  const families = new Map()
  models.forEach(model => {
    const familyId = versionFamilyId(model)
    if (!families.has(familyId)) {
      families.set(familyId, [])
    }
    families.get(familyId).push(model)
  })
  families.forEach(versions => {
    versions.sort((a, b) => versionNumber(a) - versionNumber(b))
  })
  return families
}

export function latestInFamily (versions) {
  return versions[versions.length - 1]
}

// Tables of Ad-Hoc models (Model Selection, Admin's Ad-Hoc Models tab) show one row per family, its current
// version, and when the family is expanded a row for each older version right below it, flagged
// `isOlderVersion`. They filter, sort and page those rows themselves: Vuetify would treat older versions as
// rows of their own and split a family across two pages.

// Keeps the current-version rows `matches` accepts, each with its older versions (which must follow it).
export function filterFamilyRows (rows, matches) {
  const kept = new Set()
  return rows.filter(row => {
    if (row.isOlderVersion) {
      return kept.has(versionFamilyId(row))
    }
    if (matches(row)) {
      kept.add(versionFamilyId(row))
      return true
    }
    return false
  })
}

// Sorts the current-version rows with `sortRows` and puts each one's older versions back right below it.
export function sortFamilyRows (rows, sortRows) {
  const olderVersions = new Map()
  rows.filter(row => row.isOlderVersion).forEach(row => {
    const familyId = versionFamilyId(row)
    olderVersions.set(familyId, (olderVersions.get(familyId) || []).concat(row))
  })
  const sorted = []
  sortRows(rows.filter(row => !row.isOlderVersion)).forEach(row => {
    sorted.push(row, ...(olderVersions.get(versionFamilyId(row)) || []))
  })
  return sorted
}

// How many families the rows hold: what the table's pages count.
export function countFamilyRows (rows) {
  return rows.filter(row => !row.isOlderVersion).length
}

// The rows on `page` (from 1) when a page holds `itemsPerPage` families with all their expanded versions, so
// a page may run a few rows longer but a family is never split. An itemsPerPage of -1 shows every row.
export function familyRowsPage (rows, page, itemsPerPage) {
  if (itemsPerPage < 0) {
    return rows
  }
  const first = (page - 1) * itemsPerPage
  let family = -1
  return rows.filter(row => {
    if (!row.isOlderVersion) {
      family++
    }
    return family >= first && family < first + itemsPerPage
  })
}

// The page count for `familyCount` families, at least 1 (what the table shows when there are no rows).
export function lastFamilyPage (familyCount, itemsPerPage) {
  return itemsPerPage < 0 ? 1 : Math.max(1, Math.ceil(familyCount / itemsPerPage))
}
